import { Curry, Evolver, Fn, LengthAble, MergeRight, Predicate, PropFunction, SizeAble } from '@/types/common';

export const complement = <T extends unknown[]>(fn: Predicate<T>): Predicate<T> => {
  return (...args: T) => {
    return !fn(...args);
  };
};

export const isEmpty = (value: unknown): value is null | undefined => {
  if (!value) return true;

  if (
    Array.isArray(value) ||
    isString(value) ||
    (typeof value === 'object' && 'length' in value! && value.length !== undefined)
  ) {
    return (value as unknown as LengthAble).length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return (value as unknown as SizeAble).size === 0;
  }

  if (typeof value === 'object' && !(value instanceof Function)) {
    return Object.keys(value!).length === 0;
  }

  return true;
};

export const isNotEmpty = complement(isEmpty);

export const isNil = <T>(value: T | null | undefined): value is null | undefined =>
  value == null || typeof value === 'undefined';

export const isEven = (i: number) => i % 2 === 0;

export const applyToIndex =
  <F extends Fn<any, any>>(fn: F) =>
  <T>(_: T, ix: number): ReturnType<F> =>
    fn(ix);

export const joinWithSlash = <T>(arr = [] as T[]) => arr.join('/');

export const evensOnly = applyToIndex(isEven);

export const oddsOnly = complement(evensOnly);

export const isString = <T>(val: T) => typeof val === 'string';

export const identity = <T>(args: T) => args;

export const always =
  <T>(args: T) =>
  () =>
    args;

export const not: Predicate<boolean[]> = val => !val;

export const T: Predicate<unknown[]> = () => true;

export const F = complement(T);

// use for update object field by passed func for example: evolve({ a : { b : (prev) => prev + 1  } }, { a: { b: 2 } }  ) -> { a: { b: 3 } }
export const evolve = <T extends Record<string, unknown>>(transformations: Evolver<T>, object: T): T => {
  return Object.keys(object).reduce((acc, key) => {
    const transform = transformations[key as keyof T];
    const value = object[key as keyof T];

    const transformedValue = (() => {
      if (typeof transform === 'function') {
        return (transform as (value: unknown) => unknown)(value);
      }

      if (typeof transform === 'object' && transform !== null) {
        return evolve(transform as Evolver<typeof value>, value as Record<string, unknown>);
      }

      return transform !== undefined ? transform : value;
    })() as T[keyof T];

    return { ...acc, [key]: transformedValue };
  }, {} as T);
};

export const curry = <T extends unknown[], R>(fn: (...args: T) => R): Curry<T, R> => {
  const curried = (...args: unknown[]) => {
    if (args.length >= fn.length) {
      return fn(...(args as T));
    }
    return (...moreArgs: unknown[]) => curried(...args, ...moreArgs);
  };
  return curried as Curry<T, R>;
};

export const isOneOf =
  <T extends readonly any[]>(values: T) =>
  (value: any): value is T[number] =>
    values.includes(value);

export function equals<T>(a: T, b: T): boolean;
export function equals<T>(a: T): (b: T) => boolean;

export function equals<T>(a: T, b?: T) {
  if (arguments.length < 2) {
    return (compareTo: T) => deepEqual(a, compareTo);
  }

  return deepEqual(a, b!);

  function deepEqual(x: T, y: T): boolean {
    if (x === y) return true;

    if (x == null || y == null) return false;

    if (Array.isArray(x) && Array.isArray(y)) {
      if (x.length !== y.length) return false;
      return x.every((item, index) => deepEqual(item, y[index]));
    }

    if (typeof x === 'object' && typeof y === 'object') {
      const keysX = Object.keys(x) as (keyof T)[];
      const keysY = Object.keys(y);

      if (keysX.length !== keysY.length) return false;

      return keysX.every(key => Object.prototype.hasOwnProperty.call(y, key) && deepEqual(x[key] as T, y[key] as T));
    }

    return false;
  }
}

export const either =
  <T>(firstPredicate: Predicate<T[]>, secondPredicate: Predicate<T[]>) =>
  (value: T) => {
    return firstPredicate(value) || secondPredicate?.(value);
  };

export function head<T>(arr: [T, ...unknown[]]): T;
export function head<T>(arr: T[]): T | undefined;
export function head(arr: unknown[]) {
  return isNotEmpty(arr) ? arr[0] : undefined;
}

// work only for first level depth
export const mergeRight: MergeRight = <T extends object, U extends object>(baseObj: T, newObj?: U) => {
  if (newObj === undefined) {
    return (nestedObj: U) => ({ ...baseObj, ...nestedObj }) as T & U;
  }
  return { ...baseObj, ...newObj } as T & U;
};

export const isPresentString = (str?: string) => (str ? str.trim().length > 0 : false);

export const includes = <T>(list: string | T[], value: T | string): boolean => {
  if (typeof list === 'string' && typeof value === 'string') {
    return list.includes(value);
  }

  if (Array.isArray(list)) {
    return list.includes(value as T);
  }

  return false;
};

type AnyFunc = (...args: any[]) => any;

export function flow<A, B>(value: A, args: [fn1: (input: A) => B]): B;
export function flow<A, B, C>(value: A, args: [fn1: (input: A) => B, fn2: (input: B) => C]): C;
export function flow<A, B, C, D>(value: A, args: [fn1: (input: A) => B, fn2: (input: B) => C, fn3: (input: C) => D]): D;

export function flow(value: unknown, fns: any[]): unknown {
  return fns.reduce((acc, fn) => fn(acc), value);
}

export function pipe<F1 extends AnyFunc>(f1: F1): F1;
export function pipe<F1 extends AnyFunc, F2 extends (arg: ReturnType<F1>) => any>(
  f1: F1,
  f2: F2,
): (...args: Parameters<F1>) => ReturnType<F2>;
export function pipe<
  F1 extends AnyFunc,
  F2 extends (arg: ReturnType<F1>) => any,
  F3 extends (arg: ReturnType<F2>) => any,
>(f1: F1, f2: F2, f3: F3): (...args: Parameters<F1>) => ReturnType<F3>;
export function pipe<
  F1 extends AnyFunc,
  F2 extends (arg: ReturnType<F1>) => any,
  F3 extends (arg: ReturnType<F2>) => any,
  F4 extends (arg: ReturnType<F3>) => any,
>(f1: F1, f2: F2, f3: F3, f4: F4): (...args: Parameters<F1>) => ReturnType<F4>;
export function pipe<
  F1 extends AnyFunc,
  F2 extends (arg: ReturnType<F1>) => any,
  F3 extends (arg: ReturnType<F2>) => any,
  F4 extends (arg: ReturnType<F3>) => any,
  F5 extends (arg: ReturnType<F4>) => any,
>(f1: F1, f2: F2, f3: F3, f4: F4, f5: F5): (...args: Parameters<F1>) => ReturnType<F5>;

export function pipe(...fns: AnyFunc[]) {
  return (initialValue: any) => fns.reduce((acc, fn) => fn(acc), initialValue);
}

export const prop: PropFunction = function <K extends string>(property: K, obj?: Record<K, any>) {
  if (arguments.length < 2) {
    return <T extends Record<K, any>>(obj: T) => obj[property];
  }

  return obj![property];
};

export const toLowerCase = (str = '') => str.toLowerCase();
